/**
 * Adds edge-state attributes used by the mobile scroll-hint mask. The fade is
 * only shown while more content exists beyond that edge.
 */
export interface HorizontalScrollState {
	canScroll: boolean;
	atStart: boolean;
	atEnd: boolean;
}

interface HorizontalScrollHintOptions {
	onChange?: (state: HorizontalScrollState) => void;
}

export const initialHorizontalScrollState: HorizontalScrollState = {
	canScroll: false,
	atStart: true,
	atEnd: true,
};

export const horizontalScrollHint = (
	node: HTMLElement,
	initialOptions: HorizontalScrollHintOptions = {}
) => {
	let options = initialOptions;
	let previousState = initialHorizontalScrollState;

	const update = () => {
		const overflow = node.scrollWidth - node.clientWidth;
		// Hysteresis: once scrolling, keep it enabled until the overflow clearly
		// vanishes, and only enable it once the overflow is clearly present. The
		// dead-band stops canScroll from chattering when the content width sits
		// right on the fit/overflow boundary — including the ~16px jump in
		// available width that responsive container padding introduces at a
		// breakpoint — which would otherwise flicker any layout keyed off canScroll.
		const canScroll = previousState.canScroll ? overflow > 2 : overflow > 24;
		const atStart = node.scrollLeft <= 2;
		const atEnd = !canScroll || node.scrollLeft >= overflow - 2;

		node.dataset.scrollHint = canScroll ? 'true' : 'false';
		node.dataset.scrollStart = atStart ? 'true' : 'false';
		node.dataset.scrollEnd = atEnd ? 'true' : 'false';

		const nextState = { canScroll, atStart, atEnd };
		if (
			nextState.canScroll !== previousState.canScroll ||
			nextState.atStart !== previousState.atStart ||
			nextState.atEnd !== previousState.atEnd
		) {
			previousState = nextState;
			options.onChange?.(nextState);
		}
	};

	const resizeObserver =
		typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
	resizeObserver?.observe(node);
	node.addEventListener('scroll', update, { passive: true });
	requestAnimationFrame(update);

	return {
		update(nextOptions: HorizontalScrollHintOptions = {}) {
			options = nextOptions;
			update();
		},
		destroy() {
			resizeObserver?.disconnect();
			node.removeEventListener('scroll', update);
		},
	};
};
